/**
 * Whether this pricing object carries a pre-checkout figure at all.
 *
 * This is the gate on evaluating the A/B assignment (see `utils/trips/priceWithoutFeesExperiment`):
 * a user whose payload has nothing to reduce cannot tell the two arms apart, so bucketing them
 * would only dilute the measurement.
 *
 * @param {{ displayTotal?: number }} [pricing] - A pricing object, provider discount, or
 *   passenger-type entry.
 * @returns {boolean} Whether a display total is present.
 */
export function hasDisplayTotal(pricing) {
  return pricing?.displayTotal != null;
}

/**
 * The same two rules as `displayedPrice` and `hasDisplayTotal`, over an Immutable trip.
 *
 * It lives here, beside the canonical rule, because `utils/tripFilters` is the one surface that
 * reads Immutable maps and had to spell both rules out a second time -- the comparator and its
 * gate. Two spellings of one rule are identical until the rule moves, and then the list is
 * ordered by a figure no card shows. `??` and not `||` is the part that is easy to get wrong on
 * a re-spelling: a display total of 0 is legitimate for a gratuity fare (@luisAzcuaga).
 *
 * @param {Immutable.Map} trip - A trip with a `pricing` map.
 * @param {boolean} [netted] - Whether this user is in the variant arm.
 * @returns {number|undefined} The figure to display for that trip.
 */
export function displayedPriceFromTrip(trip, netted = false) {
  const total = trip?.getIn?.(['pricing', 'total']);
  if (!netted) return total;

  return trip?.getIn?.(['pricing', 'displayTotal']) ?? total;
}

/**
 * Whether any trip in an Immutable list carries a display total, i.e. whether there is anything
 * for the experiment to change. Same gate as `hasDisplayTotal`, same reason to live here.
 *
 * @param {Immutable.List} trips - The filtered trips about to be sorted.
 * @returns {boolean} Whether at least one trip carries a display total.
 */
export function someTripHasDisplayTotal(trips) {
  return Boolean(trips?.some?.((trip) => trip?.getIn?.(['pricing', 'displayTotal']) != null));
}

/**
 * The price to render before checkout.
 *
 * INS-1288: for tenants that hold the convenience fee back until checkout ("Taxa de
 * conveniência"), the backend takes it out and ships the result as a sibling `displayTotal`,
 * leaving `total` exactly as it was. Pedágio and taxa de embarque stay inside that figure — it
 * is the total minus one fee, NOT the checkout's Subtotal row, which is smaller.
 *
 * `total` is not just a label — it is what gets charged, what the funnel posts back as
 * `seen_price`, and what the checkout renders — so surfaces that merely *display* a price read
 * `displayTotal` when it is there and the total when it is not. Everything else keeps `total`.
 *
 * Which of the two a given user gets is an A/B assignment, not a property of the payload:
 * `netted` comes from `utils/trips/priceWithoutFeesExperiment`. It defaults to `false` on purpose —
 * a surface added later that forgets to pass it renders the charged total, which is the
 * control arm and the pre-INS-1288 behaviour. The failure mode of forgetting is "no
 * experiment", never "silently netted".
 *
 * `??` and not `||` on purpose: a display total of 0 is legitimate for a gratuity fare, and
 * truthiness would fall back to the gross total precisely where the reduced figure matters.
 *
 * @param {{ displayTotal?: number, total?: number }} [pricing] - a pricing object, a provider
 *   discount, or a passenger-type entry — any shape with `total` and maybe `displayTotal`.
 * @param {boolean} [netted] - Whether this user is in the variant arm.
 * @returns {number|undefined} the figure to display, or undefined when there is none.
 */
export default function displayedPrice(pricing, netted = false) {
  if (!netted) return pricing?.total;

  return pricing?.displayTotal ?? pricing?.total;
}

/**
 * How much the backend held back from one per-passenger price: `total - displayTotal`, or 0
 * when nothing is held back (the whole control arm, and any payload without a `displayTotal`).
 *
 * Exported because two figures on the seats screen are built from raw per-seat fares rather than
 * from a pricing object -- the panel's seat-based total and the mobile seat counter -- and the
 * only way they can agree with the netted card behind them is by subtracting this same delta.
 * Deriving it twice by hand is how they came apart in the first place.
 *
 * @param {object} [pricing] - A search pricing object, BEFORE `netPricingView` runs on it.
 * @param {boolean} [netted] - Whether this user is in the variant arm.
 * @returns {number} The per-passenger delta, never negative.
 */
export function netFeePerPassenger(pricing, netted = false) {
  const netTotal = displayedPrice(pricing, netted);
  const grossTotal = pricing?.total;
  if (netTotal == null || grossTotal == null) return 0;

  return Math.max(Number(grossTotal) - Number(netTotal), 0);
}

/**
 * The same pricing object with every *displayed* figure netted by the same per-passenger
 * amount the backend took out of `total`.
 *
 * Netting only the headline is not enough: the seat panel derives a crossed-out "before"
 * price, a discount percentage and a fare allocation from `totalBeforeDiscount`, `amount`
 * and `totalBase`. Leaving those gross makes the panel believe there is a discount — it
 * renders "1 Passageiro R$ 172" above a netted R$ 151 — and, when a trip has two or more
 * passenger types, the allocation overwrites the netted total with a gross one.
 *
 * Amounts that are deltas rather than prices (`providerDiscount.amount`, `taxes`, the
 * breakdown) are left alone: subtracting a per-seat fee from a discount would be nonsense.
 *
 * Returns the object untouched when this user is in the control arm or the backend shipped no
 * `displayTotal`, so control users and tenants without it keep their exact current numbers.
 *
 * Which fields may receive the shared delta — and why `amount` is a per-provider contract, not
 * a universal one — is written out in
 * `openspec/changes/unida-net-subtotal-ab-test/design.md` ("Which fields the shared delta may be
 * applied to"). Short version: only fields whose base is the same as `total`'s (a per-passenger
 * price with the charges still inside); `taxes`, `breakdown` and `providerDiscount.amount` are
 * deltas and are never netted.
 *
 * @param {object} [pricing] - A search pricing object.
 * @param {boolean} [netted] - Whether this user is in the variant arm.
 * @returns {object} The netted view, or the original object when nothing is netted.
 */
export function netPricingView(pricing, netted = false) {
  const netTotal = displayedPrice(pricing, netted);
  const grossTotal = pricing?.total;
  if (netTotal == null || grossTotal == null) return pricing;

  const feePerPassenger = Number(grossTotal) - Number(netTotal);
  if (!(feePerPassenger > 0)) return pricing;

  /**
   * Takes the per-passenger fee out of one price.
   *
   * @param {number|undefined|null} value - A per-passenger price to net.
   * @returns {number|undefined|null} The value minus the fee, floored at 0, or it unchanged.
   */
  const net = (value) => (value == null ? value : Math.max(Number(value) - feePerPassenger, 0));

  /**
   * Nets a provider discount, rewriting `total` only on the records that already had one.
   * Spreading `total: undefined` into a record without it would make the
   * `single_trip_discount` validation in `usePricingBeforeCheckout` — which tests
   * `'total' in discount` — start accepting a record it must reject.
   *
   * The entry's OWN `displayTotal` wins when the backend ships one, and the shared per-passenger
   * delta is only the fallback. That matters for more than precision: the results card gates the
   * whole arm on the candidate it actually renders (`TripItem`, `hasDisplayTotal(displayedCandidate)`),
   * and a provider discount can win that reduce. While the entries carry no `displayTotal` the card
   * stays gross and this panel — gated on `trip.pricing`, which does have one — nets, so the two
   * disagree on the same trip. Reading the entry's own figure is what makes them converge the
   * moment the backend emits it, with no second front-end change.
   *
   * @param {object} discount - A provider discount record.
   * @returns {object} The discount, netted when it carries a total.
   */
  const netDiscount = (discount) =>
    discount && Object.prototype.hasOwnProperty.call(discount, 'total')
      ? {
          ...discount,
          total: hasDisplayTotal(discount) ? Number(discount.displayTotal) : net(discount.total),
        }
      : discount;

  return {
    ...pricing,
    total: Number(netTotal),
    totalBeforeDiscount: net(pricing.totalBeforeDiscount),
    amount: net(pricing.amount),
    totalBase: net(pricing.totalBase),
    officePrice: net(pricing.officePrice),
    providerDiscounts: Array.isArray(pricing.providerDiscounts)
      ? pricing.providerDiscounts.map(netDiscount)
      : pricing.providerDiscounts,
  };
}
